import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, Phone, ChevronDown } from "lucide-react";
import { Button } from "./ui/button";
import { boroughs } from "../data/boroughs";

const PtcLogoMark = () => (
  <svg width="44" height="44" viewBox="0 0 44 44" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <rect width="44" height="44" fill="#1a2b4a"/>
    <rect x="3" y="3" width="38" height="38" fill="none" stroke="#d4a853" strokeWidth="2"/>
    {/* P */}
    <rect x="8"    y="7"  width="2.5" height="13" fill="#d4a853"/>
    <rect x="8"    y="7"  width="7"   height="2"  fill="#d4a853"/>
    <rect x="12.5" y="7"  width="2.5" height="6.5" fill="#d4a853"/>
    <rect x="8"    y="12" width="7"   height="2"  fill="#d4a853"/>
    {/* T */}
    <rect x="18" y="7" width="9" height="2"  fill="#d4a853"/>
    <rect x="21" y="7" width="3" height="13" fill="#d4a853"/>
    {/* C */}
    <rect x="30" y="7"  width="2.5" height="13" fill="#d4a853"/>
    <rect x="30" y="7"  width="7"   height="2"  fill="#d4a853"/>
    <rect x="30" y="18" width="7"   height="2"  fill="#d4a853"/>
    {/* Divider */}
    <rect x="9" y="22" width="26" height="1" fill="#d4a853" opacity="0.4"/>
    {/* Broom handle — diagonal parallelogram, upper-right to lower-left */}
    <polygon points="29,23 31,25 21,32 19,30" fill="#d4a853"/>
    {/* Broom head — trapezoid, wider at bottom */}
    <polygon points="15,31 27,31 29,36 13,36" fill="#d4a853"/>
    {/* Bristles — varying lengths, longest in centre */}
    <rect x="14"   y="36" width="1.2" height="1.5" fill="#d4a853"/>
    <rect x="16.5" y="36" width="1.2" height="2"   fill="#d4a853"/>
    <rect x="19"   y="36" width="1.2" height="2.5" fill="#d4a853"/>
    <rect x="21.5" y="36" width="1.2" height="2.5" fill="#d4a853"/>
    <rect x="24"   y="36" width="1.2" height="2"   fill="#d4a853"/>
    <rect x="26.5" y="36" width="1.2" height="1.5" fill="#d4a853"/>
  </svg>
);

const SERVICES = [
  { label: "End of Tenancy Cleaning", path: "/end-of-tenancy-cleaning-london" },
  { label: "Deep Cleaning", path: "/deep-cleaning-london" },
  { label: "Carpet Cleaning", path: "/carpet-cleaning-london" },
  { label: "Oven Cleaning", path: "/oven-cleaning-london" },
];

const NAV_LINKS = [
  { label: "Prices", path: "/prices" },
  { label: "About", path: "/about" },
  { label: "FAQ", path: "/faq" },
  { label: "Contact", path: "/contact" },
];

export const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [boroughsOpen, setBoroughsOpen] = useState(false);
  const location = useLocation();
  const isHome = location.pathname === "/";

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    handleScroll();
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setMobileMenuOpen(false);
    setServicesOpen(false);
    setBoroughsOpen(false);
  }, [location.pathname]);

  const solid = isScrolled || !isHome;

  const dropdownClass = "bg-white border-2 border-navy py-2";
  const dropdownLinkClass = "block px-5 py-2.5 text-sm font-semibold text-navy hover:bg-navy hover:text-white transition-colors";

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        solid ? "bg-white shadow-[0_2px_0_0_#1a2b4a]" : "bg-transparent"
      }`}
      data-testid="site-header"
    >
      <div className="container mx-auto px-6 md:px-12 lg:px-20">
        <div className="flex items-center justify-between h-20">

          {/* Logo */}
          <Link to="/" className="flex items-center gap-3" data-testid="logo-link">
            <PtcLogoMark />
            <div className="flex flex-col leading-none gap-1">
              <span className={`font-body font-black text-sm tracking-[0.1em] uppercase ${solid ? "text-navy" : "text-white"}`}>
                Prime Tenancy
              </span>
              <div className={`h-[2px] w-full ${solid ? "bg-gold" : "bg-gold"}`} />
              <span className="font-body font-bold text-[9px] tracking-[0.25em] uppercase text-gold">
                Cleaning
              </span>
            </div>
          </Link>

          {/* Desktop nav */}
          <nav className="hidden lg:flex items-center gap-6">

            {/* Services dropdown */}
            <div
              className="relative"
              onMouseEnter={() => setServicesOpen(true)}
              onMouseLeave={() => setServicesOpen(false)}
            >
              <button
                className={`flex items-center gap-1 font-bold text-sm uppercase tracking-wide transition-colors hover:text-gold ${
                  solid ? "text-navy" : "text-white/90"
                }`}
                onClick={() => setServicesOpen((v) => !v)}
                aria-haspopup="true"
                aria-expanded={servicesOpen}
              >
                Services
                <ChevronDown className="w-4 h-4" />
              </button>
              <AnimatePresence>
                {servicesOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: 6 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 6 }}
                    transition={{ duration: 0.12 }}
                    className="absolute left-0 top-full pt-3 w-64"
                  >
                    <div className={dropdownClass}>
                      {SERVICES.map((s) => (
                        <Link key={s.path} to={s.path} className={dropdownLinkClass}>
                          {s.label}
                        </Link>
                      ))}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Boroughs dropdown */}
            <div
              className="relative"
              onMouseEnter={() => setBoroughsOpen(true)}
              onMouseLeave={() => setBoroughsOpen(false)}
            >
              <button
                className={`flex items-center gap-1 font-bold text-sm uppercase tracking-wide transition-colors hover:text-gold ${
                  solid ? "text-navy" : "text-white/90"
                }`}
                onClick={() => setBoroughsOpen((v) => !v)}
                aria-haspopup="true"
                aria-expanded={boroughsOpen}
              >
                Boroughs
                <ChevronDown className="w-4 h-4" />
              </button>
              <AnimatePresence>
                {boroughsOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: 6 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 6 }}
                    transition={{ duration: 0.12 }}
                    className="absolute left-0 top-full pt-3 w-[420px]"
                  >
                    <div className={`${dropdownClass} px-2`}>
                      <p className="px-3 pt-1 pb-2 text-[10px] font-black uppercase tracking-[0.2em] text-slate-400">
                        End of Tenancy Cleaning
                      </p>
                      <div className="grid grid-cols-2">
                        {boroughs.map((b) => (
                          <Link
                            key={b.slug}
                            to={`/end-of-tenancy-cleaning-${b.slug}`}
                            className={dropdownLinkClass}
                          >
                            {b.name}
                          </Link>
                        ))}
                      </div>
                      <div className="border-t-2 border-navy mt-2 pt-2 mx-3">
                        <Link
                          to="/end-of-tenancy-cleaning-london"
                          className="block py-2 text-xs font-black uppercase tracking-[0.15em] text-gold hover:text-navy transition-colors"
                        >
                          → All London
                        </Link>
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {NAV_LINKS.map((link) => (
              <NavLink
                key={link.path}
                to={link.path}
                className={({ isActive }) =>
                  `font-bold text-sm uppercase tracking-wide transition-colors hover:text-gold ${
                    isActive ? "text-gold" : solid ? "text-navy" : "text-white/90"
                  }`
                }
              >
                {link.label}
              </NavLink>
            ))}
          </nav>

          {/* Desktop right */}
          <div className="hidden lg:flex items-center gap-4">
            <a
              href="tel:+442080363586"
              className={`flex items-center gap-2 font-bold text-sm uppercase tracking-wide ${
                solid ? "text-navy" : "text-white"
              }`}
            >
              <Phone className="w-4 h-4" />
              020 8036 3586
            </a>
            <Button
              className="bg-gold hover:bg-gold-dark text-navy font-black uppercase tracking-wide px-6 rounded-none"
              asChild
            >
              <Link to="/get-a-quote">Get a Quote</Link>
            </Button>
          </div>

          {/* Mobile hamburger */}
          <button
            className="lg:hidden p-2"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Toggle menu"
          >
            {mobileMenuOpen ? (
              <X className={solid ? "text-navy" : "text-white"} />
            ) : (
              <Menu className={solid ? "text-navy" : "text-white"} />
            )}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden bg-white border-t-2 border-navy overflow-hidden"
          >
            <nav className="container mx-auto px-6 py-6 flex flex-col gap-1">

              <p className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] px-2 mt-2 mb-1">
                Services
              </p>
              {SERVICES.map((s) => (
                <Link key={s.path} to={s.path} className="text-navy font-bold py-2 px-2 uppercase text-sm tracking-wide hover:bg-navy hover:text-white transition-colors">
                  {s.label}
                </Link>
              ))}

              <div className="h-[2px] bg-navy my-3" />

              <p className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] px-2 mb-1">
                Boroughs
              </p>
              <div className="grid grid-cols-2 gap-x-2">
                {boroughs.map((b) => (
                  <Link
                    key={b.slug}
                    to={`/end-of-tenancy-cleaning-${b.slug}`}
                    className="text-navy font-semibold py-1.5 px-2 text-sm hover:bg-navy hover:text-white transition-colors"
                  >
                    {b.name}
                  </Link>
                ))}
              </div>

              <div className="h-[2px] bg-navy my-3" />

              {NAV_LINKS.map((link) => (
                <Link key={link.path} to={link.path} className="text-navy font-bold py-2 px-2 uppercase text-sm tracking-wide hover:bg-navy hover:text-white transition-colors">
                  {link.label}
                </Link>
              ))}

              <a
                href="tel:+442080363586"
                className="flex items-center gap-2 text-navy font-bold py-2 px-2 mt-2 uppercase text-sm"
              >
                <Phone className="w-4 h-4" />
                020 8036 3586
              </a>

              <Button
                className="bg-gold hover:bg-gold-dark text-navy font-black uppercase tracking-wide w-full mt-3 rounded-none"
                asChild
              >
                <Link to="/get-a-quote" onClick={() => setMobileMenuOpen(false)}>
                  Get a Quote
                </Link>
              </Button>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};
